/**
 * DviMediaCard - Individual media item display with actions
 * 
 * Features:
 * - Thumbnail with aspect ratio
 * - Media type indicator (image/video)
 * - Excluded badge overlay
 * - Selection checkbox for batch actions
 * - Action menu (view, exclude, delete)
 */

import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  MoreVertical, 
  Image as ImageIcon, 
  Video, 
  Eye, 
  EyeOff, 
  Trash2,
  X,
  Loader2
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { DviMediaItem } from '@/hooks/useDviMedia';

interface DviMediaCardProps {
  media: DviMediaItem;
  selected?: boolean;
  onSelect?: (selected: boolean) => void;
  onToggleExclude?: (excluded: boolean) => void;
  onDelete?: () => void;
  onViewFull?: () => void;
  canDelete?: boolean;
  isDeleting?: boolean;
  isToggling?: boolean;
  selectionMode?: boolean;
}

export function DviMediaCard({
  media,
  selected = false,
  onSelect,
  onToggleExclude,
  onDelete,
  onViewFull,
  canDelete = false,
  isDeleting = false,
  isToggling = false,
  selectionMode = false,
}: DviMediaCardProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const isVideo = media.media_type === 'video';
  const isExcluded = media.is_excluded;

  const handleDelete = () => {
    setShowDeleteConfirm(false);
    onDelete?.();
  };

  return (
    <>
      <Card 
        className={cn(
          'relative aspect-square overflow-hidden group cursor-pointer transition-all',
          isExcluded && 'opacity-50',
          selected && 'ring-2 ring-primary ring-offset-1',
        )}
        onClick={() => {
          if (selectionMode && onSelect) {
            onSelect(!selected);
          } else {
            onViewFull?.();
          }
        }}
      >
        {/* Thumbnail */}
        {media.signedUrl ? (
          isVideo ? (
            <video 
              src={media.signedUrl} 
              className="w-full h-full object-cover"
              muted
              playsInline
            />
          ) : (
            <img 
              src={media.signedUrl} 
              alt="DVI media" 
              className="w-full h-full object-cover"
              loading="lazy"
            />
          )
        ) : (
          <div className="w-full h-full bg-muted flex items-center justify-center">
            {isVideo ? (
              <Video className="h-8 w-8 text-muted-foreground" />
            ) : (
              <ImageIcon className="h-8 w-8 text-muted-foreground" />
            )}
          </div>
        )}

        {/* Media type badge */}
        <div className="absolute top-1 left-1 bg-black/60 text-white rounded px-1 py-0.5">
          {isVideo ? (
            <Video className="h-3 w-3" />
          ) : (
            <ImageIcon className="h-3 w-3" />
          )}
        </div>

        {/* Excluded overlay */}
        {isExcluded && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <div className="bg-red-500/80 text-white rounded-full p-2">
              <X className="h-5 w-5" />
            </div>
          </div>
        )}

        {/* Selection checkbox */}
        {selectionMode && onSelect && (
          <div className="absolute top-1 right-1 z-10" onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={selected}
              onCheckedChange={onSelect}
              className="bg-white border-2"
            />
          </div>
        )}

        {/* Action menu (visible on hover) */}
        {!selectionMode && (
          <div 
            className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity z-10"
            onClick={(e) => e.stopPropagation()}
          >
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="secondary" 
                  size="icon" 
                  className="h-6 w-6"
                  disabled={isDeleting || isToggling}
                >
                  {isDeleting || isToggling ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <MoreVertical className="h-3 w-3" />
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onViewFull?.()}>
                  <Eye className="h-4 w-4 mr-2" />
                  View Full Size
                </DropdownMenuItem>
                {onToggleExclude && (
                  <DropdownMenuItem onClick={() => onToggleExclude(!isExcluded)}>
                    {isExcluded ? (
                      <>
                        <Eye className="h-4 w-4 mr-2" />
                        Include Media
                      </>
                    ) : (
                      <>
                        <EyeOff className="h-4 w-4 mr-2" />
                        Exclude (Bad Shot)
                      </>
                    )}
                  </DropdownMenuItem>
                )}
                {canDelete && onDelete && (
                  <DropdownMenuItem 
                    onClick={() => setShowDeleteConfirm(true)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </Card>

      {/* Delete confirmation dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Media?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this {isVideo ? 'video' : 'photo'} from the inspection. 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
